import { useCallback, useEffect, useMemo, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Title, useDataProvider, useNotify, useRedirect } from "react-admin";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { formatIsoDate, formatListDate, formatWeekdayFull } from "@/utils/date-format";
import { toUtcYmd } from "@/utils/hall-slot-matches-raza-event-date";
import { useRouteId } from "@/utils/route-utility";
import {
  STOP_STATUS_COLORS,
  STOP_STATUS_LABELS,
  type HouseVisitSlotRecord,
  type HouseVisitStopRecord,
  type HouseVisitStopStatus,
} from "../types";

function resolveDateFromSearch(searchParams: URLSearchParams): string {
  const fromQuery = searchParams.get("date");
  const ymd = fromQuery ? toUtcYmd(fromQuery) : null;
  return ymd ?? formatIsoDate(dayjs(), { utc: true });
}

const DailyOpsView = () => {
  const eventId = useRouteId();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [searchParams, setSearchParams] = useSearchParams();

  const dateParam = searchParams.get("date");
  const date = resolveDateFromSearch(searchParams);

  // Keep `?date=` in the URL so returning from a stop restores the day.
  useEffect(() => {
    const normalized = toUtcYmd(dateParam ?? "") ?? null;
    if (normalized === date) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", date);
        return next;
      },
      { replace: true }
    );
  }, [date, dateParam, setSearchParams]);

  const setDate = useCallback(
    (nextRaw: string) => {
      const next = toUtcYmd(nextRaw) ?? nextRaw;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("date", next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const shiftDate = useCallback(
    (deltaDays: number) => {
      setDate(formatIsoDate(dayjs(date).add(deltaDays, "day"), { utc: true }));
    },
    [date, setDate]
  );

  const [slots, setSlots] = useState<HouseVisitSlotRecord[]>([]);
  const [slotTab, setSlotTab] = useState(0);
  const [stops, setStops] = useState<HouseVisitStopRecord[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingStops, setLoadingStops] = useState(false);

  const loadSlots = useCallback(async () => {
    if (!eventId) return;
    const day = toUtcYmd(date);
    if (!day) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const { data } = await dataProvider.getList("houseVisitSlots", {
        pagination: { page: 1, perPage: 50 },
        sort: { field: "date", order: "ASC" },
        filter: { eventId, date: day },
      });
      const forDay = (data as HouseVisitSlotRecord[]).filter((s) => toUtcYmd(s.date) === day);
      setSlots(forDay);
      setSlotTab(0);
    } catch (err: unknown) {
      notify((err as Error)?.message || "Failed to load slots", { type: "warning" });
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [dataProvider, date, eventId, notify]);

  useEffect(() => {
    void loadSlots();
  }, [loadSlots]);

  const activeSlot = slots[slotTab] ?? null;

  const loadStops = useCallback(async () => {
    if (!activeSlot?.id) {
      setStops([]);
      return;
    }
    setLoadingStops(true);
    try {
      const { data } = await dataProvider.getList("houseVisitStops", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "sortOrder", order: "ASC" },
        filter: { slotId: String(activeSlot.id) },
      });
      setStops((data as HouseVisitStopRecord[]).slice().sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err: unknown) {
      notify((err as Error)?.message || "Failed to load stops", { type: "warning" });
      setStops([]);
    } finally {
      setLoadingStops(false);
    }
  }, [activeSlot?.id, dataProvider, notify]);

  useEffect(() => {
    void loadStops();
  }, [loadStops]);

  const weekdayLabel = useMemo(() => formatWeekdayFull(date, { utc: true }), [date]);
  const capacity = Number(activeSlot?.capacity ?? 0) || 10;

  const progress = useMemo(() => {
    const total = stops.length;
    const done = stops.filter((s) => s.status && s.status !== "PENDING").length;
    const visited = stops.filter((s) => s.status === "VISITED").length;
    return { total, done, visited, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [stops]);

  const openStop = (stopId: string | number) => {
    redirect(`/houseVisitStops/${stopId}/show?date=${encodeURIComponent(date)}`);
  };

  if (!eventId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Open a house visit event first.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pb: 6, maxWidth: 1100, mx: "auto", width: "100%" }}>
      <Title title="Today's route" />

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "flex-end" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.25rem", md: "1.6rem" }, letterSpacing: "-0.02em" }}
          >
            Today&apos;s route
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {weekdayLabel} · {formatListDate(date, { utc: true })}
          </Typography>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <IconButton
            aria-label="Previous day"
            onClick={() => shiftDate(-1)}
            size="small"
            sx={{ border: 1, borderColor: "divider" }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <TextField
            type="date"
            label="Date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1, minWidth: { xs: 0, md: 180 } }}
          />
          <IconButton
            aria-label="Next day"
            onClick={() => shiftDate(1)}
            size="small"
            sx={{ border: 1, borderColor: "divider" }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      {loadingSlots ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : slots.length === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            No slot for this date
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate Mon–Sat slots from the event home, or pick another day.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CalendarMonthIcon />}
            onClick={() => redirect("/houseVisitSlots")}
          >
            Open slots
          </Button>
        </Paper>
      ) : (
        <>
          {slots.length > 1 ? (
            <Tabs
              value={slotTab}
              onChange={(_e, v) => setSlotTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {slots.map((s, i) => (
                <Tab
                  key={String(s.id)}
                  label={s.label?.trim() || `Slot ${i + 1}`}
                  sx={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
                />
              ))}
            </Tabs>
          ) : null}

          {activeSlot ? (
            <Paper
              variant="outlined"
              sx={{
                px: { xs: 1.5, md: 2 },
                py: 1.5,
                mb: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {activeSlot.label?.trim() || "Day slot"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stops.length}/{capacity} stops
                    {!loadingStops && progress.total > 0
                      ? ` · ${progress.done} done · ${progress.visited} visited`
                      : !loadingStops && stops.length === 0
                        ? " · none assigned yet"
                        : ""}
                  </Typography>
                </Stack>
                {progress.total > 0 ? (
                  <LinearProgress
                    variant="determinate"
                    value={progress.pct}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                ) : null}
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => redirect("show", "houseVisitSlots", activeSlot.id)}
                sx={{ alignSelf: { xs: "stretch", sm: "center" }, flexShrink: 0 }}
              >
                Plan stops
              </Button>
            </Paper>
          ) : null}

          {loadingStops ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : stops.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                py: { xs: 4, md: 6 },
                px: 2.5,
                textAlign: "center",
                borderStyle: "dashed",
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                No stops on this route yet
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, maxWidth: 360, mx: "auto" }}
              >
                The slot for {formatListDate(date, { utc: true })} exists. Add HOFs in the planner
                to build this day&apos;s list.
              </Typography>
              {activeSlot ? (
                <Button
                  variant="contained"
                  onClick={() => redirect("show", "houseVisitSlots", activeSlot.id)}
                  sx={{ fontWeight: 700 }}
                >
                  Add stops
                </Button>
              ) : null}
            </Paper>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {stops.map((stop) => {
                const status = (stop.status || "PENDING") as HouseVisitStopStatus;
                const address =
                  typeof stop.address === "string" && stop.address.trim()
                    ? stop.address.trim()
                    : null;
                const remarks =
                  typeof stop.remarks === "string" && stop.remarks.trim()
                    ? stop.remarks.trim()
                    : null;
                return (
                  <Card key={String(stop.id)} variant="outlined" sx={{ height: "100%" }}>
                    <CardActionArea
                      onClick={() => openStop(stop.id)}
                      sx={{ height: "100%", alignItems: "stretch" }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          py: 1.75,
                          height: "100%",
                          "&:last-child": { pb: 1.75 },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              #{stop.sortOrder + 1} · {stop.hofName || "—"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ITS {stop.hofItsNo}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={STOP_STATUS_LABELS[status] ?? status}
                            color={STOP_STATUS_COLORS[status] ?? "default"}
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                          />
                        </Stack>
                        {address ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {address}
                          </Typography>
                        ) : null}
                        {remarks ? (
                          <Paper
                            elevation={0}
                            sx={{
                              mt: "auto",
                              px: 1.25,
                              py: 1,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.25, fontWeight: 600 }}
                            >
                              Remarks
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {remarks}
                            </Typography>
                          </Paper>
                        ) : null}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DailyOpsView;
